import React, { useState, useContext, useEffect, useMemo } from "react";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaTrash,
} from "react-icons/fa";
import ProjectPreOrderLayoutWrapper from "../../components/ProjectPreOrderLayoutWrapper";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const EstimationTypeMaster = () => {
  const BASE_URL = "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    estimationTypeCode: "",
    estimationTypeName: "",
    description: "",
    isActive: true,
  });

  const [errors, setErrors] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  // ------------------------------------------------
  // localStorage values
  // ------------------------------------------------
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // ---------------------------
  // API: Next Code
  // ---------------------------
  const fetchNextCode = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/ppo_getNextEstimationTypeCode`,
        {
          headers: commonHeaders,
        }
      );

      const nextCode = res.data?.estimationTypeCode || "ES1";

      setFormData((prev) => ({
        ...prev,
        estimationTypeCode: nextCode,
      }));
    } catch (err) {
      console.error("Error fetching next code:", err);
      toast.error("Failed to get next estimation type code");

      setFormData((prev) => ({
        ...prev,
        estimationTypeCode: prev.estimationTypeCode || "ES1",
      }));
    }
  };

  // ---------------------------
  // API: List
  // ---------------------------
  const fetchEstimationList = async (searchOverride) => {
    try {
      setLoading(true);

      const search =
        searchOverride !== undefined ? searchOverride : searchTerm;

      const res = await axios.get(
        `${BASE_URL}/api/ppo_estimationList`,
        {
          params: { search },
          headers: commonHeaders,
        }
      );

      const list = res.data?.list || [];

      const mapped = list.map((item, index) => ({
        id: item.fld_EstimationTypeId ?? item.fld_EstimationTypeid ?? index + 1,
        sNo: index + 1,
        code: item.fld_EstimationTypecode ?? "",
        name: item.fld_EstimationTypename ?? "",
        description: item.fld_description ?? "",
        selected: false,
      }));

      setRecords(mapped);
    } catch (err) {
      console.error("Error fetching estimation list:", err);
      toast.error("Failed to load estimation list");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------
  // On Load
  // ---------------------------
  useEffect(() => {
    fetchNextCode();
    fetchEstimationList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  // ---------------------------
  // Pagination
  // ---------------------------
  const recordsPerPage = 20;
  const totalPages = Math.ceil(records.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentRecords = records.slice(startIndex, endIndex);

  // ---------------------------
  // Edit: click code -> load record
  // ---------------------------
  const handleEditById = async (estimationId) => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/ppo_getEstimationTypeById`,
        {
          params: { estimationId },
          headers: commonHeaders,
        }
      );

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to fetch record");
        return;
      }

      const d = res.data.data;

      setFormData({
        estimationTypeCode: d.fld_EstimationTypecode || "",
        estimationTypeName: d.fld_EstimationTypename || "",
        description: d.fld_description || "",
        isActive: d.fld_isactive === true || d.fld_isactive === 1,
      });

      setIsEdit(true);
      setEditId(estimationId);
      setShowFilters(true);
      setErrors({});
    } catch (err) {
      console.error("Fetch by id error:", err);
      toast.error(err.response?.data?.message || "Server error");
    }
  };

  // ---------------------------
  // Form helpers
  // ---------------------------
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.estimationTypeCode?.trim()) {
      newErrors.estimationTypeCode = "Estimation Type Code is required";
    }

    if (!formData.estimationTypeName?.trim()) {
      newErrors.estimationTypeName = "Estimation Type Name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---------------------------
  // Submit: Add / Update
  // ---------------------------
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        estimationTypeCode: formData.estimationTypeCode,
        estimationTypeName: formData.estimationTypeName,
        description: formData.description,
        isActive: formData.isActive,
      };

      // ✅ UPDATE
      if (isEdit && editId) {
        const res = await axios.post(
          `${BASE_URL}/api/ppo_updateEstimationType`,
          {
            ...payload,
            estimationId: editId,
          },
          {
            headers: commonHeaders,
          }
        );

        if (res.data?.success) {
          toast.success(res.data?.message || "Updated successfully");
          await fetchEstimationList();

          setIsEdit(false);
          setEditId(null);
          setFormData((prev) => ({
            ...prev,
            estimationTypeName: "",
            description: "",
            isActive: true,
          }));
          await fetchNextCode();
        } else {
          toast.error(res.data?.message || "Update failed");
        }

        return;
      }

      // ✅ ADD
      const res = await axios.post(
        `${BASE_URL}/api/ppo_addEstimationType`,
        payload,
        {
          headers: commonHeaders,
        }
      );

      if (res.data?.success) {
        toast.success(res.data?.message || "Saved successfully");

        await fetchEstimationList();

        setFormData((prev) => ({
          ...prev,
          estimationTypeName: "",
          description: "",
          isActive: true,
        }));
        await fetchNextCode();
      } else {
        toast.error(res.data?.message || "Save failed");
      }
    } catch (err) {
      console.error("Submit error:", err);
      toast.error(err.response?.data?.message || "Server error");
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------
  // Add New: clear & go add mode
  // ---------------------------
  // ---------------------------
  // Add New: clear & go add mode
  // ---------------------------
  const handleAddNew = async () => {
    setIsEdit(false);
    setEditId(null);
    setErrors({});

    setFormData((prev) => ({
      ...prev,
      estimationTypeName: "",
      description: "",
      isActive: true,
    }));

    await fetchNextCode();
    toast.info("Ready for new entry");
  };

  // ---------------------------
  // Cancel: discard changes, exit edit mode, clear errors
  // ---------------------------
  const handleCancel = async () => {
    const wasEdit = isEdit;

    setIsEdit(false);
    setEditId(null);
    setErrors({});

    setFormData((prev) => ({
      ...prev,
      estimationTypeName: "",
      description: "",
      isActive: true,
    }));

    if (wasEdit) {
      await fetchNextCode();
    }
  };

  // ---------------------------
  // Select checkbox
  // ---------------------------
  const handleSelectRecord = (id, checked) => {
    setRecords((prev) =>
      prev.map((r) => (r.id === id ? { ...r, selected: checked } : r))
    );
  };

    // ---------------------------
  // Search
  // ---------------------------
  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleSearchSubmit = () => {
    setCurrentPage(1);
    fetchEstimationList(searchTerm);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
    fetchEstimationList("");
  };

  // ---------------------------
  // DELETE single
  // ---------------------------
  // ---------------------------
  // DELETE single
  // ---------------------------
  const handleDeleteSingle = async () => {
    // Prefer the record being edited; otherwise fall back to a
    // single checkbox-selected record from the table.
    const selectedIds = records.filter((r) => r.selected).map((r) => r.id);

    let targetId = null;

    if (isEdit && editId) {
      targetId = editId;
    } else if (selectedIds.length === 1) {
      targetId = selectedIds[0];
    } else if (selectedIds.length > 1) {
      toast.info("Please select only one record to delete, or use Delete Selected");
      return;
    }

    if (!targetId) {
      toast.info("Please select a record to delete");
      return;
    }

    const ok = window.confirm(
      "Are you sure you want to delete this estimation type?"
    );
    if (!ok) return;

    try {
      setSaving(true);

      const res = await axios.post(
        `${BASE_URL}/api/ppo_deleteEstimationType`,
        {
          estimationId: targetId,
        },
        {
          headers: commonHeaders,
        }
      );

            if (res.data?.success) {
        toast.success(res.data.message || "Deleted successfully");

        await fetchEstimationList();

        // Uncheck the row we just deleted, if it was selected via checkbox
        setRecords((prev) =>
          prev.map((r) =>
            r.id === targetId ? { ...r, selected: false } : r
          )
        );

        setIsEdit(false);
        setEditId(null);
        setFormData((prev) => ({
          ...prev,
          estimationTypeName: "",
          description: "",
          isActive: true,
        }));

        await fetchNextCode();
      } else {
        toast.error(res.data?.message || "Delete failed");
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err.response?.data?.message || "Server error while deleting");
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------
  // DELETE selected
  // ---------------------------
  const handleDeleteSelected = async () => {
    const selectedIds = records.filter((r) => r.selected).map((r) => r.id);

    if (selectedIds.length === 0) {
      toast.info("Please select at least one record to delete");
      return;
    }

    const ok = window.confirm(
      `Delete ${selectedIds.length} selected record(s)?`
    );
    if (!ok) return;

    try {
      setSaving(true);

      for (const id of selectedIds) {
        await axios.post(
          `${BASE_URL}/api/ppo_deleteEstimationType`,
          {
            estimationId: id,
          },
          {
            headers: commonHeaders,
          }
        );
      }

      toast.success("Selected records deleted successfully");

      await fetchEstimationList();

      if (isEdit && selectedIds.includes(editId)) {
        setIsEdit(false);
        setEditId(null);
        setFormData((prev) => ({
          ...prev,
          estimationTypeName: "",
          description: "",
          isActive: true,
        }));
        await fetchNextCode();
      }
    } catch (err) {
      console.error("Bulk delete error:", err);
      toast.error("Error deleting selected records");
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------
  // Pagination UI helpers
  // ---------------------------
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    const half = Math.floor(maxVisiblePages / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxVisiblePages - 1);

    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push("...");
    }

    for (let i = start; i <= end; i++) pages.push(i);

    if (end < totalPages) {
      if (end < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <ProjectPreOrderLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ESTIMATION TYPE MASTER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Estimation Type Master
              </h1>

              {isEdit && (
                <span
                  className={`text-xs font-semibold px-3 py-1 rounded-full ${
                    isDark
                      ? "bg-yellow-500/20 text-yellow-200"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  EDIT MODE
                </span>
              )}
            </div>

                       <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search code, name, description..."
                  className={`px-3 py-2 rounded-lg border text-sm w-64 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                />
                <button
                  onClick={handleSearchSubmit}
                  className={`px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white text-sm`}
                >
                  Search
                </button>
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className={`px-4 py-2 rounded-lg font-semibold border transition-all duration-200 text-sm ${
                      isDark
                        ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                        : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300"
                    }`}
                  >
                    Clear
                  </button>
                )}
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          <div
            className={`mb-6 rounded-lg border ${
              isDark ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-gray-50"
            }`}
          >
            {showFilters && (
              <div className="p-6 border-t border-gray-200 dark:border-gray-700">
                <div className="max-w-2xl mx-auto space-y-4">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Estimation Type Code <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-8">
                      <input
                        type="text"
                        value={formData.estimationTypeCode}
                        disabled
                        className={`w-full px-4 py-2 border rounded-lg transition-all opacity-80 cursor-not-allowed ${
                          errors.estimationTypeCode ? "border-red-500" : ""
                        } ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-gray-100 border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                        placeholder="Auto generated..."
                      />
                      {errors.estimationTypeCode && (
                        <p className="text-red-500 text-sm mt-1">{errors.estimationTypeCode}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Estimation Type Name <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-8">
                      <input
                        type="text"
                        value={formData.estimationTypeName}
                        onChange={(e) =>
                          handleInputChange("estimationTypeName", e.target.value)
                        }
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          errors.estimationTypeName ? "border-red-500" : ""
                        } ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                        placeholder="Enter estimation type name..."
                      />
                      {errors.estimationTypeName && (
                        <p className="text-red-500 text-sm mt-1">{errors.estimationTypeName}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Description
                      </label>
                    </div>
                    <div className="col-span-8">
                      <input
                        type="text"
                        value={formData.description}
                        onChange={(e) => handleInputChange("description", e.target.value)}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                        placeholder="Enter description..."
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4"></div>
                    <div className="col-span-8">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="isActive"
                          checked={formData.isActive}
                          onChange={(e) => handleInputChange("isActive", e.target.checked)}
                          className={`mr-2 rounded focus:ring-2 focus:ring-blue-500 ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-blue-600"
                              : "bg-white border-gray-300 text-blue-600"
                          }`}
                        />
                        <label
                          htmlFor="isActive"
                          className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                        >
                          Is Active
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-center gap-4 pt-4 flex-wrap">
                    <button
                      onClick={handleSubmit}
                      disabled={saving}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                        saving ? "opacity-70 cursor-not-allowed" : ""
                      } ${buttonClass} ${hoverClass} text-white`}
                    >
                      <FaCheck />
                      {saving ? "Saving..." : isEdit ? "Update" : "Submit"}
                    </button>

                    <button
                      onClick={handleCancel}
                      disabled={saving}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                        saving ? "opacity-70 cursor-not-allowed" : ""
                      } ${
                        isDark
                          ? "bg-gray-600 hover:bg-gray-500 text-white"
                          : "bg-gray-300 hover:bg-gray-400 text-gray-800"
                      }`}
                    >
                      Cancel
                    </button>

                    <button
                      onClick={handleAddNew}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                    >
                      <FaPlus />
                      Add New
                    </button>

                    <button
                      onClick={handleDeleteSingle}
                      className="flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 bg-red-600 hover:bg-red-700 text-white"
                      title="Delete the currently selected (edit) record"
                    >
                      <FaTrash />
                      Delete
                    </button>

                    <button
                      onClick={handleDeleteSelected}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                        isDark
                          ? "bg-red-500/80 hover:bg-red-600 text-white"
                          : "bg-red-500 hover:bg-red-600 text-white"
                      }`}
                      title="Delete selected rows"
                    >
                      <FaTrash />
                      Delete Selected
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      S.No
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Estimation Type Code
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Estimation Type Name
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Description
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">Select</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td
                        colSpan="5"
                        className={`px-4 py-6 text-center text-sm font-medium ${
                          isDark ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Loading estimation types...
                      </td>
                    </tr>
                  ) : currentRecords.length === 0 ? (
                    <tr>
                      <td
                        colSpan="5"
                        className={`px-4 py-6 text-center text-sm font-medium ${
                          isDark ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        No estimation types found
                      </td>
                    </tr>
                  ) : (
                    currentRecords.map((record, index) => (
                      <tr
                        key={record.id}
                        className={`transition-colors duration-200 ${
                          index % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-gray-50"
                            : isDark
                            ? "bg-gray-900"
                            : "bg-white"
                        }`}
                      >
                        <td
                          className={`px-4 py-3 text-center text-sm border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {record.sNo}
                        </td>

                        <td
                          className={`px-4 py-3 text-center text-sm font-semibold border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-blue-300" : "text-blue-600"
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => handleEditById(record.id)}
                            className="underline hover:opacity-80"
                            title="Click to edit"
                          >
                            {record.code}
                          </button>
                        </td>

                        <td
                          className={`px-4 py-3 text-center text-sm border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {record.name}
                        </td>

                        <td
                          className={`px-4 py-3 text-center text-sm border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {record.description || "-"}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={record.selected}
                            onChange={(e) => handleSelectRecord(record.id, e.target.checked)}
                            className={`rounded focus:ring-2 focus:ring-blue-500 ${
                              isDark
                                ? "bg-gray-700 border-gray-600 text-blue-600"
                                : "bg-white border-gray-300 text-blue-600"
                            }`}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

                    <div className="flex justify-start mt-6">
            <div className="flex gap-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                  currentPage === 1
                    ? isDark
                      ? "bg-gray-800 text-gray-500 border-gray-700 cursor-not-allowed"
                      : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                    : isDark
                    ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Prev
              </button>

              {renderPageNumbers().map((page, index) => (
                <button
                  key={index}
                  onClick={() => page !== "..." && handlePageChange(page)}
                  disabled={page === "..."}
                  className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                    page === currentPage
                      ? "bg-blue-600 text-white border-blue-600"
                      : page === "..."
                      ? isDark
                        ? "bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed"
                        : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                      : isDark
                      ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                  currentPage === totalPages || totalPages === 0
                    ? isDark
                      ? "bg-gray-800 text-gray-500 border-gray-700 cursor-not-allowed"
                      : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                    : isDark
                    ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Next
              </button>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPreOrderLayoutWrapper>
  );
};

export default EstimationTypeMaster;